import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { sanitize } from '../lib/sanitize';
import { discountLabel } from '../lib/discountLabel';
import { useStoreSettings } from '../hooks/useStoreSettings';
import { addToCart } from '../store/cartSlice';
import { stripHtml } from '../utils/textUtils';
import SeoHead from '../components/seo/SeoHead';
import styles from '../styles/CategoryPage.module.css';

interface Product {
  id: string;
  name: string;
  price: number;
  old_price?: number;
  image_url: string;
  slug: string;
  short_description: string;
  stock_quantity: number;
}

interface Filters {
  minPrice: number;
  maxPrice: number;
  onSale: boolean;
  inStock: boolean;
}

type SortOption = 'name-asc' | 'name-desc' | 'price-asc' | 'price-desc';

interface Category {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  short_description: string | null;
  slug: string;
  show_sorting: boolean;
  show_filters: boolean;
}

const ITEMS_PER_PAGE = 12;

const CategoryPage = () => {
  const { category } = useParams<{ category: string }>();
  const { formatPrice, settings } = useStoreSettings();
  const dispatch = useDispatch();
  const quickAdd = (settings?.header_content as any)?.categoryQuickAdd === true;
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const getQty = (id: string) => quantities[id] ?? 1;
  const changeQty = (id: string, delta: number) =>
    setQuantities(prev => ({ ...prev, [id]: Math.max(1, (prev[id] ?? 1) + delta) }));
  const handleQuickAdd = (e: React.MouseEvent, product: Product) => {
    e.preventDefault();
    e.stopPropagation();
    const qty = getQty(product.id);
    dispatch(addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url || '/placeholder-product.jpg',
      quantity: qty,
    }));
    toast.success(`${product.name} (${qty} kom) dodato u korpu!`);
  };
  const [categoryData, setCategoryData] = useState<Category | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const [filters, setFilters] = useState<Filters>({
    minPrice: 0,
    maxPrice: 1000000,
    onSale: false,
    inStock: false
  });

  useEffect(() => {
    const fetchCategoryAndProducts = async () => {
      try {
        // Prvo dohvatamo kategoriju
        const { data: categoryData, error: categoryError } = await supabase
          .from('categories')
          .select('*')
          .eq('slug', category)
          .eq('is_visible', true)
          .single();

        if (categoryError) throw categoryError;
        if (!categoryData) throw new Error('Category not found');

        setCategoryData(categoryData);

        // Zatim dohvatamo proizvode za tu kategoriju preko product_categories
        // (many-to-many): proizvod se može pojaviti u više kategorija.
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('*, product_categories!inner(category_id)')
          .eq('product_categories.category_id', categoryData.id);

        if (productsError) throw productsError;
        
        setProducts(productsData || []);
        setFilteredProducts(productsData || []);
      } catch (err: any) {
        console.error('Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryAndProducts();
  }, [category]);

  // Apply filters and sorting
  useEffect(() => {
    let result = [...products];

    // Apply filters
    result = result.filter(product => {
      const matchesPrice = product.price >= filters.minPrice && product.price <= filters.maxPrice;
      const matchesSale = !filters.onSale || (product.old_price && product.old_price > product.price);
      const matchesStock = !filters.inStock || product.stock_quantity > 0;
      return matchesPrice && matchesSale && matchesStock;
    });

    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'price-asc':
          return a.price - b.price;
        case 'price-desc':
          return b.price - a.price;
        default:
          return 0;
      }
    });

    setFilteredProducts(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [products, filters, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleFilterChange = (name: keyof Filters, value: number | boolean) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Rezervǐsi visinu dok se učitava da footer ne skoči (CLS).
  if (loading) return <div style={{ minHeight: '100vh', padding: 40, textAlign: 'center' }}>Učitavanje...</div>;
  if (error) return <div style={{ minHeight: '100vh', padding: 40, textAlign: 'center' }}>Greška: {error}</div>;
  if (!categoryData) return <div style={{ minHeight: '100vh', padding: 40, textAlign: 'center' }}>Kategorija nije pronađena</div>;

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Početna', item: typeof window !== 'undefined' ? window.location.origin + '/' : undefined },
      { '@type': 'ListItem', position: 2, name: 'Kategorije', item: typeof window !== 'undefined' ? window.location.origin + '/categories' : undefined },
      { '@type': 'ListItem', position: 3, name: categoryData.name },
    ],
  };

  const itemListLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: products.slice(0, 30).map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: p.name,
      url: typeof window !== 'undefined' ? `${window.location.origin}/p/${p.slug || p.id}` : undefined,
    })),
  };

  // Gornja granica cene za filter, izvedena iz stvarnih cena u kategoriji
  // (zaokruženo naviše na 100). Fallback 1000 ako nema proizvoda.
  const priceCeiling = Math.ceil(Math.max(1000, ...products.map(p => p.price)) / 100) * 100;
  const maxPriceDisplay = Math.min(filters.maxPrice, priceCeiling);

  return (
    <div className={styles.categoryPage}>
      <SeoHead
        title={categoryData.name}
        titleOverride={(categoryData as any).seo_title}
        description={(categoryData as any).seo_description || categoryData.short_description || categoryData.description || undefined}
        image={(categoryData as any).seo_image_url || categoryData.image_url || undefined}
        noindex={Boolean((categoryData as any).noindex)}
        jsonLd={[breadcrumbLd, itemListLd]}
      />
      <div className={styles.header}>
        <nav className={styles.breadcrumbs}>
          <Link to="/">Početna</Link>
          <span className={styles.separator}>/</span>
          <Link to="/categories">Kategorije</Link>
          <span className={styles.separator}>/</span>
          <span className={styles.current}>{category}</span>
        </nav>
        <h1>{categoryData.name}</h1>
        {categoryData.short_description && (
          <div 
            className={styles.shortDescription}
            dangerouslySetInnerHTML={{ __html: sanitize(categoryData.short_description) }}
          />
        )}
      </div>

      <div className={styles.pageContent}>
        {/* Prikazujemo sidebar samo ako su filteri omogućeni */}
        {categoryData.show_filters && (
          <aside className={styles.sidebar}>
            <div className={styles.filters}>
              <h2>Filteri</h2>
              <div className={styles.filterSection}>
                <h3>Cena</h3>
                <div className={styles.priceFilter}>
                  <input
                    type="range"
                    min="0"
                    max={priceCeiling}
                    step="100"
                    aria-label="Minimalna cena"
                    value={Math.min(filters.minPrice, priceCeiling)}
                    onChange={(e) => handleFilterChange('minPrice', Number(e.target.value))}
                  />
                  <input
                    type="range"
                    min="0"
                    max={priceCeiling}
                    step="100"
                    aria-label="Maksimalna cena"
                    value={maxPriceDisplay}
                    onChange={(e) => handleFilterChange('maxPrice', Number(e.target.value))}
                  />
                  <span>{formatPrice(filters.minPrice)} - {formatPrice(maxPriceDisplay)}</span>
                </div>
              </div>

              <div className={styles.filterSection}>
                <h3>Dostupnost</h3>
                <div className={styles.checkboxFilters}>
                  <label>
                    <input
                      type="checkbox"
                      checked={filters.onSale}
                      onChange={(e) => handleFilterChange('onSale', e.target.checked)}
                    />
                    Na akciji
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={filters.inStock}
                      onChange={(e) => handleFilterChange('inStock', e.target.checked)}
                    />
                    Na stanju
                  </label>
                </div>
              </div>
            </div>
          </aside>
        )}

        <main className={`${styles.mainContent} ${!categoryData.show_filters ? styles.fullWidth : ''}`}>
          {/* Prikazujemo sortiranje samo ako je omogućeno */}
          {categoryData.show_sorting && (
            <div className={styles.topBar}>
              <div className={styles.sorting}>
                <select
                  value={sortBy}
                  aria-label="Sortiraj proizvode"
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                >
                  <option value="name-asc">Naziv (A do Š)</option>
                  <option value="name-desc">Naziv (Š do A)</option>
                  <option value="price-asc">Cena (od niže ka višoj)</option>
                  <option value="price-desc">Cena (od više ka nižoj)</option>
                </select>
              </div>
            </div>
          )}

          <div className={styles.productsGrid}>
            {paginatedProducts.map((product, idx) => (
              <Link
                to={`/p/${product.slug}`}
                key={product.id}
                className={styles.productCard}
              >
                <div className={styles.imageWrapper}>
                  <img
                    src={product.image_url}
                    srcSet={
                      product.image_url && /\.jpe?g$/i.test(product.image_url)
                        ? `${product.image_url.replace(/\.(jpe?g)$/i, '-400.$1')} 400w, ${product.image_url} 1000w`
                        : undefined
                    }
                    alt={product.name}
                    width={400}
                    height={400}
                    sizes="(max-width: 600px) 50vw, (max-width: 1024px) 33vw, 220px"
                    loading={idx < 3 ? 'eager' : 'lazy'}
                    fetchPriority={idx === 0 ? 'high' : undefined}
                    decoding="async"
                  />
                  {product.old_price && (
                    <span className={styles.discount}>
                      {Math.round(((product.old_price - product.price) / product.old_price) * 100)}%{' '}
                      {discountLabel(settings?.language)}
                    </span>
                  )}
                  {quickAdd && (
                    <div
                      className={styles.quickAdd}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    >
                      <div className={styles.qtyStepper}>
                        <button
                          type="button"
                          className={styles.qtyBtn}
                          aria-label="Smanji količinu"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); changeQty(product.id, -1); }}
                        >
                          −
                        </button>
                        <span className={styles.qtyVal}>{getQty(product.id)}</span>
                        <button
                          type="button"
                          className={styles.qtyBtn}
                          aria-label="Povećaj količinu"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); changeQty(product.id, 1); }}
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        className={styles.addBtn}
                        onClick={(e) => handleQuickAdd(e, product)}
                      >
                        Dodaj u korpu
                      </button>
                    </div>
                  )}
                </div>
                <div className={styles.productInfo}>
                  <h3>{product.name}</h3>
                  <p className={styles.description}>{product.short_description}</p>
                  <div className={styles.priceBox}>
                    <span className={styles.currentPrice}>
                      {formatPrice(product.price)}
                    </span>
                    {product.old_price && (
                      <span className={styles.oldPrice}>
                        {formatPrice(product.old_price)}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Prethodna
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={currentPage === i + 1 ? styles.active : ''}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Sledeća
              </button>
            </div>
          )}
        </main>
      </div>

      {categoryData.description && (
        <div className={styles.fullDescription}>
          <div
            className={styles.descriptionContent}
            dangerouslySetInnerHTML={{ __html: sanitize(categoryData.description) }}
          />
        </div>
      )}

      {products.length === 0 && (
        <div className={styles.noProducts}>
          No products found in this category
        </div>
      )}
    </div>
  );
};

export default CategoryPage; 